import React, { useRef } from 'react';

import { useTranslation } from '@lyearn/i18n';
import { cn } from '@lyearn/style';
import { Stack, Typography } from '@lyearn/ui';
import { QuizLanguageInfoFragment } from '@/modules/corporate/modules/quiz/views/QuizEditor/graphql/fragments/generated/QuizLanguageInfo';
import {
  CourseContentType,
  JobEntityStatusEnum,
  LanguageEnum,
  Maybe,
  ResourceStatusEnum,
  ResourceTypeEnum,
} from '@/types/schema';

import { CourseLanguageInfoFragment } from '../../../../graphql/fragments/generated/CourseLanguageInfo';
import { AddButton } from './AddButton';
import { SyncNowButton } from './SyncNowButton';
import { getLanguageName, LanguageSelectorProps } from '.';

type DropdownItemProps = LanguageSelectorProps & {
  defaultLanguage: LanguageEnum;
  content: CourseLanguageInfoFragment | QuizLanguageInfoFragment;
  adaptedLanguageConfigs: CourseContentType['languageConfigs'];
  language: LanguageEnum;
  sourceContent:
    | {}
    | {
        id: string;
        lastContentEditTimeAt?: string | null;
        languageType?: LanguageEnum | null;
        defaultLanguageType?: LanguageEnum | null;
        languageConfigs?: Array<{
          contentId: string;
          languageType: LanguageEnum;
        }> | null;
      }
    | null
    | undefined;
  isLocalizedContent: boolean;
  localizedContentIds: Partial<Record<LanguageEnum, string>>;
  onChangeDefaultLanguage: (language: any) => Promise<void>;
  setCurrentLanguage: React.Dispatch<React.SetStateAction<Maybe<LanguageEnum> | undefined>>;
  onNavigationToLanguage: (contentId?: string) => void;
  off: () => void;
  onToggle: (open?: boolean) => void;
};

function DropdownItem({
  defaultLanguage,
  adaptedLanguageConfigs,
  jobStatus,
  language,
  content,
  sourceContent,
  isLocalizedContent,
  localizedContents,
  localizedContentIds,
  onChangeDefaultLanguage,
  setCurrentLanguage,
  refetchQuery,
  refetchLocalizedContent,
  onNavigationToLanguage,
  off,
  onToggle,
}: DropdownItemProps) {
  const { t } = useTranslation();
  const prevTranslationStatus = useRef(jobStatus[language]);
  const translationStatus = jobStatus[language];

  const presentInLanguageConfigs = adaptedLanguageConfigs?.find(
    (config) => config.languageType === language,
  );

  const isInDraftState = content?.status === ResourceStatusEnum.Draft;

  const showAddButton =
    (!(
      presentInLanguageConfigs || (sourceContent as CourseContentType)?.languageType === language
    ) &&
      !isInDraftState &&
      !isLocalizedContent) ||
    presentInLanguageConfigs?.jobStatus === JobEntityStatusEnum.InProcess;

  const showDraftButton =
    (
      localizedContents?.contents?.edges?.find(
        (edge) => (edge.node as CourseContentType)?.id === presentInLanguageConfigs?.contentId,
      )?.node as CourseContentType
    )?.status == ResourceStatusEnum.Draft &&
    !isLocalizedContent &&
    presentInLanguageConfigs?.jobStatus === JobEntityStatusEnum.Completed;

  const syncedAt = (
    localizedContents?.contents?.edges?.find(
      (edge) => (edge.node as CourseContentType)?.id === presentInLanguageConfigs?.contentId,
    )?.node as CourseContentType
  )?.sourceContentSyncedAt;

  const isSyncedContentVerified = (
    localizedContents?.contents?.edges?.find(
      (edge) => (edge.node as CourseContentType)?.id === presentInLanguageConfigs?.contentId,
    )?.node as CourseContentType
  )?.isSyncedContentVerified;

  const lastContentEditTimeAt = content?.isLocalizedContent
    ? (sourceContent as CourseContentType)?.lastContentEditTimeAt
    : content?.lastContentEditTimeAt;

  const isSynced =
    lastContentEditTimeAt &&
    syncedAt &&
    new Date(syncedAt as string).getTime() > new Date(lastContentEditTimeAt as string).getTime();

  const showTranslationInProgress =
    !isInDraftState &&
    presentInLanguageConfigs &&
    isSyncedContentVerified === false &&
    isSynced &&
    !isLocalizedContent &&
    !showDraftButton;

  const showTranslationCompleted =
    !isInDraftState &&
    presentInLanguageConfigs &&
    isSyncedContentVerified === true &&
    isSynced &&
    !isLocalizedContent &&
    !showDraftButton &&
    !showAddButton;

  const showSyncNowButton =
    !isInDraftState &&
    presentInLanguageConfigs &&
    content.type !== ResourceTypeEnum.Path &&
    !showTranslationInProgress &&
    !showTranslationCompleted &&
    !isSynced &&
    !isLocalizedContent &&
    !showDraftButton &&
    translationStatus !== JobEntityStatusEnum.InProcess;

  const showAdded =
    !isInDraftState &&
    !showAddButton &&
    !showSyncNowButton &&
    !showTranslationInProgress &&
    !showTranslationCompleted &&
    !isLocalizedContent &&
    !showDraftButton;

  const configContentId = adaptedLanguageConfigs?.find((config) => config.languageType === language)
    ?.contentId;
  const localizedContentId = localizedContentIds[language];
  const contentId = (configContentId || localizedContentId) as string;
  return (
    <Stack
      key={language}
      alignItems="center"
      className={cn(
        '!px-8 !py-8 hover:bg-bg-hover-light rounded-8 min-h-[44px]',
        presentInLanguageConfigs?.jobStatus === JobEntityStatusEnum.InProcess
          ? 'cursor-not-allowed'
          : 'cursor-pointer',
      )}
      justifyContent="between"
      onClick={() => {
        if (presentInLanguageConfigs?.jobStatus === JobEntityStatusEnum.InProcess) {
          return;
        } else if (isInDraftState && !isLocalizedContent) {
          onChangeDefaultLanguage(language);
          setCurrentLanguage(language);
          off();
        } else {
          onToggle(false);
          onNavigationToLanguage(contentId);
          // onClickLanguage(language);
        }
      }}>
      <Stack alignItems="center" gapX={8}>
        <Typography variant="label-01">{getLanguageName(language)}</Typography>
        {language == defaultLanguage ? (
          <Typography color="text-text-secondary" variant="body-long-03">
            (default)
          </Typography>
        ) : null}
      </Stack>
      {showAddButton && (
        <AddButton
          content={content}
          jobScheduleId={
            presentInLanguageConfigs?.jobStatus === JobEntityStatusEnum.InProcess
              ? presentInLanguageConfigs?.jobScheduleId ?? null
              : null
          }
          language={language}
          refetchQuery={refetchQuery}
        />
      )}
      {showSyncNowButton && (
        <SyncNowButton
          content={content}
          language={language}
          lnaguageConfig={presentInLanguageConfigs}
          refetchLocalizedContent={refetchLocalizedContent}
        />
      )}
      {showTranslationInProgress && translationStatus === JobEntityStatusEnum.InProcess && (
        <Typography className="text-text-secondary mr-12" variant="body-short-03">
          {t('Course:TranslationInProgress')}
        </Typography>
      )}
      {showTranslationInProgress && translationStatus !== JobEntityStatusEnum.InProcess && (
        <Typography className="text-text-secondary mr-12" variant="body-short-03">
          {t('Course:VerificationPending')}
        </Typography>
      )}
      {showTranslationCompleted && (
        <Typography className="text-text-secondary mr-12" variant="body-short-03">
          {t('Course:TranslationCompleted')}
        </Typography>
      )}
      {showAdded && (
        <Typography className="text-text-secondary mr-12" variant="body-short-03">
          {t('Course:Added')}
        </Typography>
      )}
      {showDraftButton && (
        <Typography className="text-text-secondary mr-12" variant="body-short-03">
          {t('Course:Draft')}
        </Typography>
      )}
    </Stack>
  );
}

export default DropdownItem;
